import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useApolloClient } from '@apollo/client';
import { Location, PaymentStatus } from 'types/payments';
import config from 'utils/config';
import { buildCustomerFromBillingDetails } from 'utils/booking';
import { getBookingJourneysURL } from 'utils/urls';
import { historyShape, legShape, passengersShape } from 'constants/propTypes';
import { ACTION_CODES } from 'constants/errors';
import { COMPLETE_VONAGE_PAYMENT, UPDATE_CUSTOMER } from 'store/mutations';
import { GET_MEMBERSHIP_DETAILS } from 'store/queries';
import { Spinner } from 'admin-components/components';
import usePayIfLoyaltyAndConfirmBookingMutation from 'components/mutations/usePayIfLoyaltyAndConfirmBookingMutation';
import AgentPortalError from 'errors/AgentPortalError';
import VonageFrameWrapper from './VonageFrameWrapper';

import { Popup, PopupWrapper, PaymentLoadingOverlay } from './CardPaymentPopup.styles';

const vonageIframeUrl = config('VONAGE_IFRAME_URL');

const CardPaymentPopup = ({
  isOpen,
  onClose,
  userEmail,
  totalPriceToBePaid,
  paymentAmount,
  reference: bookingReference,
  currency,
  billingDetails,
  isPackage,
  hasHotel,
  isPutOnHoldWithDeposit,
  isSplitPayment,
  history,
  passengers,
  outbound,
  inbound,
  isThalys,
  intl: { formatMessage }
}) => {
  const popupWrapper = useRef();
  const postMessageListener = useRef();
  const client = useApolloClient();
  const [completeVonagePayment, { loading: completeVonagePaymentLoading, error: completeVonagePaymentError }] = useMutation(
    COMPLETE_VONAGE_PAYMENT,
    {
      refetchQueries: ['booking']
    }
  );
  const [updateCustomer, { loading: updateCustomerLoading, error: updateCustomerError }] = useMutation(UPDATE_CUSTOMER);
  const [confirmBooking, { loading: confirmBookingLoading, error: confirmBookingError }] = usePayIfLoyaltyAndConfirmBookingMutation();

  const [paymentError, setPaymentError] = useState('');

  const { email, countryCode, city, postalCode, stateOrProvince, addressLine1, addressLine2 } = billingDetails;
  const shouldPutOnHold = isPutOnHoldWithDeposit && paymentAmount === totalPriceToBePaid;
  const membershipNumber = new URLSearchParams(history.location.search).get('membershipNumber');
  const isMissedDeparture = new URLSearchParams(history.location.search).get('isMissedDeparture') === 'true';
  const isFlowForwardCapped = new URLSearchParams(history.location.search).get('isFlowForwardCapped') === 'true';
  const isSubscription = new URLSearchParams(history.location.search).get('isSubscription') === 'true';

  const handleUpdateCustomer = async () => {
    let customerInfo = null;
    if (membershipNumber) {
      try {
        const result = await client.query({
          query: GET_MEMBERSHIP_DETAILS,
          variables: {
            membershipNumber
          }
        });
        customerInfo = result?.data?.getCustomerDetails;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Cannot get customer info', error);
      }
    }

    const customer = customerInfo
      ? {
          firstName: customerInfo?.firstName || '',
          lastName: customerInfo?.lastName || '',
          email: customerInfo?.emailAddress || '',
          phoneNumber: customerInfo?.phoneNumber || '',
          address: {
            street: customerInfo?.address?.street || '',
            postalCode: customerInfo?.address?.postalCode || '',
            city: customerInfo?.address?.city || '',
            countryCode: customerInfo?.address?.countryCode || ''
          }
        }
      : buildCustomerFromBillingDetails(billingDetails);

    return updateCustomer({
      variables: {
        bookingReference,
        customer
      }
    });
  };

  const onAfterPayment = async () => {
    const isTotalPrice = paymentAmount === totalPriceToBePaid;
    const isConfirmationRequired = !isPutOnHoldWithDeposit && isTotalPrice && isSplitPayment;
    const isRedirectRequired = isTotalPrice || !isSplitPayment || shouldPutOnHold;

    if (isConfirmationRequired) {
      await confirmBooking({
        variables: {
          bookingData: {
            reference: bookingReference,
            currency,
            passengers,
            outbound,
            inbound
          },
          membershipNumber,
          isMissedDeparture,
          isFlowForwardCapped
        }
      });
    }

    if (isRedirectRequired) {
      const bookingJourneysURL = getBookingJourneysURL(bookingReference, membershipNumber);
      history.push(bookingJourneysURL);
    }

    onClose();
  };

  const onVonagePaymentComplete = async (event) => {
    if (event.origin !== vonageIframeUrl) {
      return;
    }

    const {
      data: { VariableData: paymentResult }
    } = event;
    const resultCode = paymentResult.resultCode.Value;

    if (!resultCode) {
      onClose();

      return;
    }

    if (resultCode !== PaymentStatus.AUTHORISED) {
      const refusalReason = paymentResult.refusalReason.Value;
      if ([PaymentStatus.PCI_PAL_ERROR, PaymentStatus.TIMEOUT].includes(resultCode)) {
        setPaymentError(formatMessage({ id: 'payments.vonageServerError' }, { resultCode, refusalReason }));
      } else {
        setPaymentError(formatMessage({ id: 'payments.vonagePaymentError' }, { resultCode, refusalReason }));
      }

      return;
    }

    if (!isSubscription) {
      await handleUpdateCustomer();
    }

    await completeVonagePayment({
      variables: {
        bookingReference,
        isDepositPayment: shouldPutOnHold,
        isSplitPayment,
        isMissedDeparture,
        isFlowForwardCapped,
        currency: paymentResult.currency.Value,
        amount: parseFloat((paymentResult.value.Value / 100).toFixed(2)),
        paymentData: {
          hasHotel: isPackage || hasHotel,
          location: Location.EBSC,
          pspReference: paymentResult.pspReference.Value,
          cardSummary: paymentResult.cardSummary.Value
        }
      }
    });
    await onAfterPayment();
  };

  useEffect(() => {
    if (isOpen) {
      postMessageListener.current = onVonagePaymentComplete;
      window.addEventListener('message', postMessageListener.current, false);
    } else {
      window.removeEventListener('message', postMessageListener.current);
    }

    return () => {
      window.removeEventListener('message', postMessageListener.current);
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const isLoading = completeVonagePaymentLoading || updateCustomerLoading || confirmBookingLoading;
  const error = updateCustomerError || confirmBookingError || completeVonagePaymentError;
  if (paymentError) {
    throw new AgentPortalError(paymentError, ACTION_CODES.DEFAULT_ACTION, {
      additionalMessage: paymentError
    });
  }
  if (error) {
    throw new AgentPortalError(error);
  }

  return (
    <PopupWrapper ref={popupWrapper} isOpen={isOpen}>
      <Popup id="card-payment-popup" isOpen={isOpen}>
        {isOpen ? (
          <>
            <VonageFrameWrapper
              agentEmail={userEmail}
              bookingReference={bookingReference}
              currency={currency}
              amount={paymentAmount}
              city={city}
              country={countryCode}
              houseNumberOrName={addressLine1}
              postalCode={postalCode}
              street={addressLine2 || addressLine1}
              stateOrProvince={stateOrProvince}
              hasHotel={isPackage || hasHotel}
              location={Location.EBSC}
              shopperEmail={email}
              isThalys={isThalys}
            />
            {isLoading && (
              <PaymentLoadingOverlay>
                <Spinner />
              </PaymentLoadingOverlay>
            )}
          </>
        ) : null}
      </Popup>
    </PopupWrapper>
  );
};

CardPaymentPopup.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  userEmail: PropTypes.string,
  totalPriceToBePaid: PropTypes.number.isRequired,
  paymentAmount: PropTypes.number.isRequired,
  reference: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  billingDetails: PropTypes.shape({
    addressLine1: PropTypes.string,
    addressLine2: PropTypes.string,
    addressLine3: PropTypes.string,
    city: PropTypes.string,
    countryCode: PropTypes.string,
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    phoneCountryCode: PropTypes.string,
    phoneNumber: PropTypes.string,
    postalCode: PropTypes.string,
    stateOrProvince: PropTypes.string
  }),
  isPackage: PropTypes.bool,
  hasHotel: PropTypes.bool,
  isPutOnHoldWithDeposit: PropTypes.bool,
  isSplitPayment: PropTypes.bool,
  history: historyShape,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired
  }).isRequired,
  outbound: PropTypes.arrayOf(legShape),
  inbound: PropTypes.arrayOf(legShape),
  passengers: passengersShape,
  isThalys: PropTypes.bool
};

export default CardPaymentPopup;
